import React from 'react';
import { Code2, Database, Lightbulb, ArrowRight, BookOpen, ShieldCheck, FileCode, HelpCircle, Image as ImageIcon, GitMerge, Monitor, Cpu } from 'lucide-react';
export function InvestorsSection() {
  const stats = [{
    value: '10',
    label: 'Vakgebieden'
  }, {
    value: '16',
    label: 'Niveaus'
  }, {
    value: '17+',
    label: 'Vraagtypes'
  }, {
    value: '100+',
    label: 'Skills'
  }, {
    value: '15+',
    label: 'Visuele hulpmiddelen'
  }, {
    value: '6',
    label: 'Staps pipeline'
  }, {
    value: '4',
    label: 'Gebruikersrollen'
  }, {
    value: '4',
    label: 'Leermodi'
  }];
  const pipelineSteps = [{
    name: 'Schema Validatie',
    icon: <FileCode className="w-4 h-4" data-id="element-117" />
  }, {
    name: 'Vraagtype Check',
    icon: <HelpCircle className="w-4 h-4" data-id="element-118" />
  }, {
    name: 'Didactische Inhoud',
    icon: <BookOpen className="w-4 h-4" data-id="element-119" />
  }, {
    name: 'Visuele Support',
    icon: <ImageIcon className="w-4 h-4" data-id="element-120" />
  }, {
    name: 'Hints & Feedback',
    icon: <Lightbulb className="w-4 h-4" data-id="element-121" />
  }, {
    name: 'Leerpad Integriteit',
    icon: <GitMerge className="w-4 h-4" data-id="element-122" />
  }];
  const architectureLayers = [{
    title: 'Laag 1 — Gebruikersinterface',
    icon: <Monitor className="w-4 h-4" data-id="element-123" />,
    items: ['HomePage', 'Learning Lab', 'Oefensessies', 'Toetsen', 'Voortgang', 'Portalen'],
    color: 'text-blue-400'
  }, {
    title: 'Laag 2 — Kern Engines',
    icon: <Cpu className="w-4 h-4" data-id="element-124" />,
    items: ['Adaptive Engine', 'Mastery Engine', 'Assessment Engine', 'Mode Engine', 'Placement Engine'],
    color: 'text-indigo-400'
  }, {
    title: 'Laag 3 — Data & Governance',
    icon: <Database className="w-4 h-4" data-id="element-125" />,
    items: ['Taxonomy Registry', 'Question Loader', 'Seed Consolidator', '6-Stage Pipeline', 'Visual Registry', 'LDG'],
    color: 'text-purple-400'
  }];
  const techBadges = ['React', 'TypeScript', 'Tailwind CSS', 'Framer Motion', 'LocalStorage'];
  return <section id="technologie" className="py-16 lg:py-24 bg-slate-950 text-slate-300" data-id="element-126">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-127">
        <div className="max-w-3xl mb-16" data-id="element-128">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-6 tracking-tighter" data-id="element-129">
            Onder de motorkap 🔧
          </h2>
          <p className="text-lg text-slate-400 leading-relaxed" data-id="element-130">
            Een robuuste architectuur en een schaalbaar content-model. Ontdek
            hoe Meester djay de nieuwste technologieën inzet voor een naadloze
            en adaptieve leerervaring.
          </p>
        </div>

        {/* Big Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-20 pb-12 border-b border-slate-800" data-id="element-131">
          {stats.map((stat, idx) => <div key={idx} data-id="element-132">
              <div className="text-4xl md:text-5xl font-extrabold text-white mb-2 tracking-tighter" data-id="element-133">
                {stat.value}
              </div>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-134">
                {stat.label}
              </div>
            </div>)}
        </div>

        <div className="grid lg:grid-cols-2 gap-16 mb-16" data-id="element-135">
          {/* Governance Pipeline Subsection */}
          <div data-id="element-136">
            <div className="flex items-center gap-3 mb-4" data-id="element-137">
              <ShieldCheck className="w-6 h-6 text-orange-400" data-id="element-138" />
              <h3 className="text-xl font-bold text-white" data-id="element-139">
                6-Stage Kwaliteitspipeline
              </h3>
            </div>
            <p className="text-sm text-slate-400 mb-8 leading-relaxed" data-id="element-140">
              Elke vraag doorloopt een streng geautomatiseerd proces. Vragen die
              niet door alle 6 filters komen, worden automatisch geblokkeerd
              voor leerlingen.
            </p>
            <div className="flex flex-wrap gap-3" data-id="element-141">
              {pipelineSteps.map((step, index) => <div key={index} className="flex items-center gap-2 bg-slate-900 border border-slate-800 rounded-full px-4 py-2" data-id="element-142">
                  <span className="text-slate-500" data-id="element-143">{step.icon}</span>
                  <span className="text-xs font-bold text-slate-300" data-id="element-144">
                    {step.name}
                  </span>
                </div>)}
            </div>
          </div>

          {/* Architecture Subsection */}
          <div data-id="element-145">
            <div className="flex items-center gap-3 mb-4" data-id="element-146">
              <Code2 className="w-6 h-6 text-purple-400" data-id="element-147" />
              <h3 className="text-xl font-bold text-white" data-id="element-148">
                Technische Architectuur
              </h3>
            </div>
            <p className="text-sm text-slate-400 mb-8 leading-relaxed" data-id="element-149">
              Een modulaire, client-side architectuur ontworpen voor snelheid,
              privacy en schaalbaarheid.
            </p>
            <div className="space-y-4 mb-8" data-id="element-150">
              {architectureLayers.map((layer, idx) => <div key={idx} className="flex flex-col sm:flex-row sm:items-start gap-4" data-id="element-151">
                  <div className="flex items-center gap-3 min-w-[220px]" data-id="element-152">
                    <span className={layer.color} data-id="element-153">{layer.icon}</span>
                    <h4 className="text-sm font-bold text-white" data-id="element-154">
                      {layer.title}
                    </h4>
                  </div>
                  <div className="flex flex-wrap gap-x-2 gap-y-1" data-id="element-155">
                    {layer.items.map((item, iIdx) => <span key={iIdx} className="text-xs text-slate-400" data-id="element-156">
                        {item}
                        {iIdx < layer.items.length - 1 ? ',' : ''}
                      </span>)}
                  </div>
                </div>)}
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-157">
              {techBadges.map((badge, idx) => <span key={idx} className="px-3 py-1.5 rounded-full bg-slate-800 text-slate-300 text-xs font-bold" data-id="element-158">
                  {badge}
                </span>)}
            </div>
          </div>
        </div>

        <div className="pt-12 border-t border-slate-800" data-id="element-159">
          <a href="mailto:mick@mvlcreative.com" className="inline-flex items-center gap-2 bg-white text-slate-950 hover:bg-slate-100 px-6 py-3 rounded-lg font-bold text-sm transition-colors" data-id="element-160">
            Neem contact op
            <ArrowRight className="w-4 h-4" data-id="element-161" />
          </a>
        </div>
      </div>
    </section>;
}